import React, { useState, useEffect, useRef } from 'react';
import { User, MapPin, Target, Award, Instagram, Shield, ArrowLeft, CheckCircle, Camera, Upload, X, Eye, EyeOff, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { playerAPI } from '../../services/community';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const EditPlayerProfile = ({ player, onBack, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const fileInputRef = useRef(null);
  const [options, setOptions] = useState({
    experience_levels: [],
    positions: [],
    play_styles: [],
  });

  const [formData, setFormData] = useState({
    name: player?.name || '',
    nickname: player?.nickname || '',
    location: player?.location || '',
    experience_level: player?.experience_level || 'Beginner',
    favorite_position: player?.favorite_position || '',
    primary_marker: player?.primary_marker || '',
    years_playing: player?.years_playing || '',
    play_style: player?.play_style || '',
    home_field: player?.home_field || '',
    championships: player?.championships || '',
    awards: player?.awards || '',
    instagram_handle: player?.instagram_handle || '',
    bio: player?.bio || '',
    past_teams: player?.past_teams || '',
    profile_picture: player?.profile_picture || '',
    profile_password: '', // Required for verification
  });

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const data = await playerAPI.getOptions();
        setOptions(data);
      } catch (err) {
        console.error('Error fetching options:', err);
      }
    };
    fetchOptions();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a valid image file (JPG, PNG, GIF, or WebP)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image file must be less than 5MB');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('type', 'profile');

      const response = await fetch(`${API_BASE_URL}/api/uploads/image?type=profile`, {
        method: 'POST',
        body: uploadFormData,
      });

      if (!response.ok) throw new Error('Upload failed');

      const data = await response.json();
      setFormData(prev => ({ ...prev, profile_picture: `${API_BASE_URL}${data.url}` }));
    } catch (err) {
      setError('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = () => {
    setFormData(prev => ({ ...prev, profile_picture: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.profile_password) {
      setError('Please enter your profile password to save changes');
      return;
    }

    setLoading(true);

    try {
      const { ...submitData } = formData;
      submitData.years_playing = submitData.years_playing ? parseInt(submitData.years_playing) : null;
      
      const result = await playerAPI.update(player.player_id, submitData);
      setSuccess(true);
      
      // Update localStorage
      localStorage.setItem('gwins_player', JSON.stringify(result.player));
      
      if (onSuccess) {
        setTimeout(() => onSuccess(result.player), 1500);
      }
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#0a0b0c] flex items-center justify-center p-4">
        <div className="bg-[#131416] rounded-2xl p-8 max-w-md w-full text-center border border-[#00d4aa]/30">
          <CheckCircle className="h-16 w-16 text-[#00d4aa] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Profile Updated!</h2>
          <p className="text-white/60 mb-4">Your changes have been saved.</p>
          <p className="text-sm text-white/40">Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0b0c] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={onBack}
            className="text-white/60 hover:text-white mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-3xl font-black text-white uppercase">Edit Profile</h1>
          <p className="text-white/60 mt-2">Update your player profile information</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400">
              {error}
            </div>
          )}

          {/* Profile Photo */}
          <div className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center">
              <Camera className="h-5 w-5 mr-2 text-[#00d4aa]" />
              Profile Photo
            </h2>
            
            <div className="flex items-center gap-6">
              <div className="relative">
                <div className="w-28 h-28 rounded-full bg-[#1a1c1f] border-2 border-dashed border-white/20 flex items-center justify-center overflow-hidden">
                  {formData.profile_picture ? (
                    <img src={formData.profile_picture} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <User className="h-10 w-10 text-white/30" />
                  )}
                </div>
                {formData.profile_picture && (
                  <button
                    type="button"
                    onClick={removePhoto}
                    className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    <X className="h-4 w-4 text-white" />
                  </button>
                )}
              </div>
              <div className="flex-1 space-y-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="border-[#00d4aa]/50 text-[#00d4aa] hover:bg-[#00d4aa]/10 w-full"
                >
                  {uploading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-[#00d4aa] border-t-transparent rounded-full animate-spin mr-2" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Change Photo
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Basic Info */}
          <div className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center">
              <User className="h-5 w-5 mr-2 text-[#00d4aa]" />
              Basic Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Name</label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  className="bg-[#1a1c1f] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Nickname / Callsign</label>
                <Input
                  name="nickname"
                  value={formData.nickname}
                  onChange={handleChange}
                  placeholder="e.g., Thunder, Sniper"
                  className="bg-[#1a1c1f] border-white/10 text-white"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-white/70 text-sm mb-2">Location</label>
                <Input
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="City, State/Country"
                  className="bg-[#1a1c1f] border-white/10 text-white"
                />
              </div>
            </div>
          </div>

          {/* Experience & Play Style */}
          <div className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center">
              <Target className="h-5 w-5 mr-2 text-[#00d4aa]" />
              Experience & Play Style
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Experience Level</label>
                <select
                  name="experience_level"
                  value={formData.experience_level}
                  onChange={handleChange}
                  className="w-full bg-[#1a1c1f] border border-white/10 text-white rounded-md px-3 py-2"
                >
                  {options.experience_levels.map(level => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Favorite Position</label>
                <select
                  name="favorite_position"
                  value={formData.favorite_position}
                  onChange={handleChange}
                  className="w-full bg-[#1a1c1f] border border-white/10 text-white rounded-md px-3 py-2"
                >
                  <option value="">Select Position</option>
                  {options.positions.map(pos => (
                    <option key={typeof pos === 'object' ? pos.value : pos} value={typeof pos === 'object' ? pos.value : pos}>
                      {typeof pos === 'object' ? pos.value : pos}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Play Style</label>
                <select
                  name="play_style"
                  value={formData.play_style}
                  onChange={handleChange}
                  className="w-full bg-[#1a1c1f] border border-white/10 text-white rounded-md px-3 py-2"
                >
                  <option value="">Select Style</option>
                  {options.play_styles.map(style => (
                    <option key={typeof style === 'object' ? style.value : style} value={typeof style === 'object' ? style.value : style}>
                      {typeof style === 'object' ? style.value : style}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Years Playing</label>
                <Input
                  name="years_playing"
                  type="number"
                  value={formData.years_playing}
                  onChange={handleChange}
                  placeholder="e.g., 5"
                  className="bg-[#1a1c1f] border-white/10 text-white"
                  min="0"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-white/70 text-sm mb-2">Primary Marker</label>
                <Input
                  name="primary_marker"
                  value={formData.primary_marker}
                  onChange={handleChange}
                  placeholder="e.g., Planet Eclipse CS3, Dye M3+"
                  className="bg-[#1a1c1f] border-white/10 text-white"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-white/70 text-sm mb-2">Home Field</label>
                <Input
                  name="home_field"
                  value={formData.home_field}
                  onChange={handleChange}
                  placeholder="e.g., Hollywood Sports Park"
                  className="bg-[#1a1c1f] border-white/10 text-white"
                />
              </div>
            </div>
          </div>

          {/* Achievements */}
          <div className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center">
              <Award className="h-5 w-5 mr-2 text-[#00d4aa]" />
              Achievements & History
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Championships & Titles</label>
                <Input
                  name="championships"
                  value={formData.championships}
                  onChange={handleChange}
                  placeholder="e.g., NXL World Cup 2023 Champion"
                  className="bg-[#1a1c1f] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Awards & Achievements</label>
                <Input
                  name="awards"
                  value={formData.awards}
                  onChange={handleChange}
                  placeholder="e.g., MVP NXL Chicago 2023"
                  className="bg-[#1a1c1f] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Past Teams</label>
                <Input
                  name="past_teams"
                  value={formData.past_teams}
                  onChange={handleChange}
                  placeholder="e.g., Dynasty 2018-2020"
                  className="bg-[#1a1c1f] border-white/10 text-white"
                />
              </div>
            </div>
          </div>

          {/* Social & Bio */}
          <div className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center">
              <Instagram className="h-5 w-5 mr-2 text-[#00d4aa]" />
              Social & Bio
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Instagram Handle</label>
                <Input
                  name="instagram_handle"
                  value={formData.instagram_handle}
                  onChange={handleChange}
                  placeholder="@yourhandle"
                  className="bg-[#1a1c1f] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Bio</label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  placeholder="Tell us about yourself..."
                  rows={4}
                  className="w-full bg-[#1a1c1f] border border-white/10 text-white rounded-md px-3 py-2 resize-none"
                />
              </div>
            </div>
          </div>

          {/* Password Verification */}
          <div className="bg-[#131416] rounded-xl p-6 border border-[#00d4aa]/30">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center">
              <Shield className="h-5 w-5 mr-2 text-[#00d4aa]" />
              Verify Identity
            </h2>
            
            <div>
              <label className="block text-white/70 text-sm mb-2">Profile Password *</label>
              <div className="relative">
                <Input
                  name="profile_password"
                  type={showPassword ? "text" : "password"}
                  value={formData.profile_password}
                  onChange={handleChange}
                  placeholder="Enter your profile password to save"
                  className="bg-[#1a1c1f] border-white/10 text-white pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-white/40 text-xs mt-1">Required to save changes</p>
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              className="flex-1 border-white/20 text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c] font-bold"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-[#0a0b0c] border-t-transparent rounded-full animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  SAVE CHANGES
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPlayerProfile;
